import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { IconButton, Divider, Tooltip, Typography, Box as MuiBox } from '@mui/material';
import Box from '@mui/material/Box';
import { useState, useEffect } from 'react';

import Drawer from '@/shared/components/core-ui/drawer/drawer.component';
import { useLocalization } from '@/shared/hooks/useLocalization';

import SideMenuContent from './components/side-menu-content';

interface SideMenuProps {
  mobileOpen: boolean;
  setMobileOpen: (mobileOpen: boolean) => void;
}

const MENU_COLLAPSE_KEY = 'sideMenuCollapsed';

const openCommandPalette = () => window.dispatchEvent(new CustomEvent('open-command-palette'));

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform);
const shortcutLabel = isMac ? '⌘K' : 'Ctrl+K';

const SearchHint = ({ isCollapsed, isRTL }: { isCollapsed: boolean; isRTL: boolean }) => {
  if (isCollapsed) {
    return (
      <Tooltip title={`Search pages (${shortcutLabel})`} placement={isRTL ? 'left' : 'right'}>
        <IconButton
          onClick={openCommandPalette}
          size="small"
          sx={{
            width: 36,
            height: 36,
            borderRadius: '8px',
            color: 'text.secondary',
            mx: 'auto',
            display: 'flex',
            '&:hover': { bgcolor: 'primary.light', color: 'primary.main' }
          }}
        >
          <SearchRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <MuiBox
      onClick={openCommandPalette}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 0.875,
        borderRadius: '8px',
        border: '1px solid',
        borderColor: 'grey.200',
        bgcolor: 'grey.50',
        cursor: 'pointer',
        transition: 'all 0.15s',
        '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.light' },
        '&:hover .search-icon': { color: 'primary.main' },
        '&:hover .search-shortcut': { borderColor: 'primary.main', color: 'primary.main' }
      }}
    >
      <SearchRoundedIcon
        className="search-icon"
        sx={{ fontSize: 15, color: 'text.secondary', flexShrink: 0 }}
      />
      <Typography
        sx={{ flex: 1, fontSize: 12.5, color: 'text.secondary', userSelect: 'none' }}
        noWrap
      >
        Search pages...
      </Typography>
      <MuiBox
        className="search-shortcut"
        sx={{
          px: 0.75,
          py: '2px',
          border: '1px solid',
          borderColor: 'grey.300',
          borderRadius: '5px',
          fontSize: '10px',
          fontWeight: 700,
          color: 'text.secondary',
          lineHeight: 1.6,
          fontFamily: 'monospace',
          flexShrink: 0,
          transition: 'all 0.15s'
        }}
      >
        {shortcutLabel}
      </MuiBox>
    </MuiBox>
  );
};

const SideMenu = ({ mobileOpen, setMobileOpen }: SideMenuProps) => {
  const { isRTLDirect: isRTL } = useLocalization();

  const [isCollapsed, setIsCollapsed] = useState(() => {
    const savedState = localStorage.getItem(MENU_COLLAPSE_KEY);
    return savedState ? JSON.parse(savedState) : false;
  });

  const expandedWidth = 256;
  const collapsedWidth = 64;
  const drawerWidth = isCollapsed ? collapsedWidth : expandedWidth;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleCollapseToggle = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem(MENU_COLLAPSE_KEY, JSON.stringify(newState));
  };

  // Sync collapse state with localStorage when component mounts
  useEffect(() => {
    const savedState = localStorage.getItem(MENU_COLLAPSE_KEY);
    if (savedState !== null) {
      setIsCollapsed(JSON.parse(savedState));
    }
  }, []);

  return (
    <Box
      component="nav"
      sx={{
        width: { xs: 0, md: drawerWidth },
        flexShrink: { xs: 0 },
        transition: 'width 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms'
      }}
      aria-label="SideMenu"
    >
      <Drawer
        config={{
          width: drawerWidth,
          anchor: isRTL ? 'right' : 'left',
          isOpen: mobileOpen,
          appearOverAppbar: false
        }}
        eventHandlers={{ handleClose: handleDrawerToggle }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <SideMenuContent isCollapsed={isCollapsed} />
          <Box sx={{ marginTop: 'auto' }}>
            <Divider />
            <Box sx={{ px: 1.5, py: 1 }}>
              <SearchHint isCollapsed={isCollapsed} isRTL={isRTL} />
            </Box>
            <Divider />
            <Box sx={{ p: 1, display: 'flex', justifyContent: isRTL ? 'flex-start' : 'flex-end' }}>
              <IconButton onClick={handleCollapseToggle}>
                {isCollapsed ? (
                  isRTL ? (
                    <ChevronLeftIcon />
                  ) : (
                    <ChevronRightIcon />
                  )
                ) : isRTL ? (
                  <ChevronRightIcon />
                ) : (
                  <ChevronLeftIcon />
                )}
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Drawer>
      <Drawer
        config={{
          width: drawerWidth,
          variant: 'permanent',
          anchor: isRTL ? 'right' : 'left',
          appearOverAppbar: false
        }}
        styles={{
          display: { xs: 'none', md: 'flex' },
          '& .MuiDrawer-paper': {
            transition: 'width 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms',
            overflowX: 'hidden',
            width: drawerWidth
          }
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <SideMenuContent isCollapsed={isCollapsed} />
          <Box sx={{ marginTop: 'auto' }}>
            <Divider />
            <Box sx={{ px: 1.5, py: 1 }}>
              <SearchHint isCollapsed={isCollapsed} isRTL={isRTL} />
            </Box>
            <Divider />
            <Box
              sx={{
                p: 1,
                display: 'flex',
                justifyContent: isCollapsed ? 'center' : isRTL ? 'flex-start' : 'flex-end'
              }}
            >
              <IconButton
                onClick={handleCollapseToggle}
                sx={{
                  bgcolor: 'background.paper',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              >
                {isCollapsed ? (
                  isRTL ? (
                    <ChevronLeftIcon color="primary" />
                  ) : (
                    <ChevronRightIcon color="primary" />
                  )
                ) : isRTL ? (
                  <ChevronRightIcon color="primary" />
                ) : (
                  <ChevronLeftIcon color="primary" />
                )}
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default SideMenu;
